/**
 * Prefetches tooltip and help-screen images into the service worker's runtime
 * cache ('help-images') so they are available offline. These images are
 * excluded from the SW precache so installing the SW doesn't delay the
 * sign-in button becoming clickable; this warm-up runs after sign-in instead.
 */

const IMAGE_MARKDOWN_SOURCES = [
  () => import('../data/local-files/tooltips-optimized.md?raw'),
  () => import('../pages/help/md-files/helpfile_general.md?raw'),
  () => import('../pages/help/md-files/helpfile_undercarriage.md?raw'),
  () => import('../pages/help/md-files/helpfile_bucket.md?raw'),
];

const IMAGE_MARKDOWN_REGEX = /!\[[^\]]*\]\(([^)]+)\)/g;

// Must match the runtime cache the SW serves these images from (vite configs).
const HELP_IMAGES_CACHE = 'help-images';

async function getHelpImageUrls(): Promise<string[]> {
  const urls = new Set<string>();

  for (const loadSource of IMAGE_MARKDOWN_SOURCES) {
    try {
      const fileContent = (await loadSource()).default;
      for (const match of fileContent.matchAll(IMAGE_MARKDOWN_REGEX)) {
        const url = match[1]?.trim();
        if (
          url &&
          (url.includes('images/tooltips/') ||
            url.includes('images/helpfiles/'))
        ) {
          urls.add(url);
        }
      }
    } catch {
      // Source file missing from the bundle — nothing to prefetch for it.
    }
  }

  return [...urls];
}

/**
 * Fetch tooltip/help images so the SW runtime cache stores them for offline
 * use. Fire-and-forget friendly: failures (e.g. offline) are logged and
 * ignored — StaleWhileRevalidate picks the images up on demand later.
 */
export async function prefetchHelpImages(concurrency = 6): Promise<void> {
  const imageUrls = await getHelpImageUrls();
  if (imageUrls.length === 0) {
    return;
  }

  const startTime = performance.now();
  const queue = [...imageUrls];
  // Responses are written to the cache directly rather than relying on the
  // SW's runtime caching rule: on a fresh install the page is not yet
  // controlled by the SW, so its fetches bypass that rule entirely.
  const cache = 'caches' in self ? await caches.open(HELP_IMAGES_CACHE) : null;
  let fetchedCount = 0;
  let failedCount = 0;

  const worker = async () => {
    for (let url = queue.shift(); url; url = queue.shift()) {
      try {
        // Already-cached images are skipped so repeat app starts don't
        // re-download them; StaleWhileRevalidate refreshes them when a
        // tooltip or help page is actually viewed.
        if (cache && (await caches.match(url))) {
          continue;
        }
        const response = await fetch(url);
        if (!response.ok) {
          failedCount++;
          continue;
        }
        if (cache) {
          await cache.put(url, response);
        }
        fetchedCount++;
      } catch {
        failedCount++;
      }
    }
  };

  await Promise.all(Array.from({ length: concurrency }, worker));

  const duration = Math.round(performance.now() - startTime);
  if (failedCount > 0) {
    console.warn(
      `⚠️ Help image prefetch: ${failedCount}/${imageUrls.length} failed in ${duration}ms`,
    );
  } else {
    console.log(
      `✅ Help images prefetched: ${fetchedCount} fetched, ${imageUrls.length - fetchedCount} already cached, in ${duration}ms`,
    );
  }
}
